/**
 * Visit Close Out Action - WHAT Section
 *
 * Edit mode UI for configuring automatic visit close-out.
 * Used inline within AutomationCard when editing the "What" section.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-visit-close-out.hbs
 *
 * Fields:
 * - content: Array of service IDs to close out (empty = all services)
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { fetchServicesOptions } from 'src/models/service/service';
import { BaseSelect } from 'src/shared/Forms/Selects/BaseSelect';

import { ActionWhatSectionProps } from '../automationActions';

/**
 * VisitCloseOut WHAT Section - Edit mode
 */
export const VisitCloseOutWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  // Fetch available services
  const { data: services = [], isLoading } = useQuery({
    queryKey: ['services', 'options'],
    queryFn: () => fetchServicesOptions(),
    staleTime: 60000,
  });

  const serviceOptions = services.map((s) => ({
    id: s.id.toString(),
    label: s.name,
  }));

  const selectedServices = Array.isArray(automation.content)
    ? automation.content
    : [];

  return (
    <Stack spacing={2}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.visitCloseOut.services')}</Box>
        <Box sx={{ minWidth: 250, flex: 1 }}>
          <BaseSelect
            options={serviceOptions}
            value={selectedServices}
            onChange={(value) => {
              setFieldValue(`automations.${index}.content`, value);
            }}
            placeholder={isLoading ? t('automations.common.loading') : t('automations.visitCloseOut.allServices')}
            multiple
            valueOnly
          />
        </Box>
      </Box>
    </Stack>
  );
};

/**
 * VisitCloseOut What Summary Component
 */
export const VisitCloseOutWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const { data: services = [] } = useQuery({
    queryKey: ['services', 'options'],
    queryFn: () => fetchServicesOptions(),
    staleTime: 60000,
  });

  const selectedServices = Array.isArray(automation.content)
    ? automation.content
    : [];

  if (selectedServices.length === 0) {
    return <>{t('automations.visitCloseOut.allServices')}</>;
  }

  const serviceNames = selectedServices
    .map((id) => services.find((s) => s.id.toString() === id.toString())?.name)
    .filter(Boolean)
    .join(', ');

  return <>{serviceNames || t('automations.visitCloseOut.allServices')}</>;
};
